import React, {
  createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react'
import { Navigate, useLocation, useParams } from 'react-router-dom'
import { AxiosError } from 'axios'
import { extractCountryFromDepartureOffice } from 'shared/ncts/common/classifier-util'
import { NctsCountry } from '../../../../config'
import { toIntOrNull } from '../../../../helpers'
import { useFetchConsignment } from '../../consignment/api/fetchConsignment'
import { TransitOperation } from '../../transit-operation/models/types'
import { Consignment } from '../../consignment/models/types'
import { CreateOrUpdateTransitOperationRequestDeclarationTypeEnum } from '../../../../api/models'
import { NctsScope } from '../../../../shared/ncts/types'
import { useFetchTransitOperation } from '../../transit-operation/api/fetchTransitOperation'
import { ServerValidationResult } from '../../models/types'

export type DeclarationData = {
  transitOperation: TransitOperation | undefined
  consignment: Consignment | undefined
}

export type DeclarationEditorData = {
  id?: number
  security?: string
  inlandModeOfTransport?: string
  borderModeOfTransport?: string
  departureCustomsOffice?: string
  country?: NctsCountry | null
  declarationType?: string
  additionalDeclarationType?: string
  specificCircumstanceIndicator?: string
  countriesOfRouting?: string[]
  isDraft?: boolean
}

export type DeclarationValidationContextValue = {
  security: string
  isEns: boolean
  additionalDeclarationType: string
  specificCircumstanceIndicator: string
  country: NctsCountry | null
  isDraft: boolean
}

type ServerValidationResultForScope = {
  [scope: string]: ServerValidationResult | null
}

export type DeclarationEditorContextValue = {
  declarationData: DeclarationData
  editorData: DeclarationEditorData | undefined
  isLoading: boolean
  updateTransitEditorData: (partial: Partial<DeclarationEditorData>) => void
  serverValidationResult: ServerValidationResultForScope | null
  setServerValidationResultForScope: (scope: NctsScope, requirements: ServerValidationResult | null) => void
  setIsCustomsSubmission: (isCustomsSubmission: boolean) => void
  validationContextRef: React.MutableRefObject<DeclarationValidationContextValue | null>
}

const DeclarationEditorContext = createContext<DeclarationEditorContextValue | undefined>(undefined)

function is404(error: unknown) {
  return error instanceof AxiosError && error.response?.status === 404
}

export function DeclarationEditorContextProvider({
  isEns = false,
  children,
}: Readonly<{
  isEns?: boolean
  children: ReactNode
}>) {
  const location = useLocation()
  const { transitId } = useParams()

  const [declarationEditorData, setDeclarationEditorData] = useState<DeclarationEditorData | undefined>(undefined)
  const [
    serverValidationResult,
    setServerValidationResult,
  ] = useState<ServerValidationResultForScope | undefined>(undefined)

  const validationContextRef = useRef<DeclarationValidationContextValue>({
    security: '0',
    isEns,
    additionalDeclarationType: 'A',
    specificCircumstanceIndicator: '',
    country: null,
    isDraft: true,
  })
  const transitOperationId = toIntOrNull(transitId)

  const { data: transitOperation, isFetching, error } = useFetchTransitOperation(transitOperationId, isEns)
  const { data: consignment, isFetching: isFetchingConsignment } = useFetchConsignment(transitOperationId)

  useEffect(() => {
    let declarationType = transitOperation?.declarationType
    if (!declarationType) {
      declarationType = isEns
        ? CreateOrUpdateTransitOperationRequestDeclarationTypeEnum.ENS
        : CreateOrUpdateTransitOperationRequestDeclarationTypeEnum.T1
    }

    const security = transitOperation?.security ?? '0'
    const additionalDeclarationType = transitOperation?.additionalDeclarationType ?? 'A'
    const specificCircumstanceIndicator = transitOperation?.specificCircumstanceIndicator ?? ''
    const departureCustomsOffice = transitOperation?.departureCustomsOffice ?? ''
    const country = extractCountryFromDepartureOffice(departureCustomsOffice ?? null)

    setDeclarationEditorData({
      id: transitOperation?.id ?? undefined,
      security,
      declarationType,
      additionalDeclarationType,
      specificCircumstanceIndicator,
      departureCustomsOffice,
      country,
      inlandModeOfTransport: consignment?.inlandModeOfTransport,
      borderModeOfTransport: consignment?.borderModeOfTransport,
      countriesOfRouting: consignment?.countryOfRoutingOfConsignment?.length
        ? consignment?.countryOfRoutingOfConsignment
          .filter((c) => c.country != null)
          .map((c) => c.country as string) : [],
      isDraft: true,
    })

    validationContextRef.current.security = security
    validationContextRef.current.additionalDeclarationType = additionalDeclarationType
    validationContextRef.current.specificCircumstanceIndicator = specificCircumstanceIndicator
    validationContextRef.current.country = country
    validationContextRef.current.isEns = isEns
  }, [transitOperation, consignment, isEns])

  const setIsCustomsSubmission = useCallback((isCustomsSubmission: boolean) => {
    validationContextRef.current.isDraft = !isCustomsSubmission
    setDeclarationEditorData((prev) => ({
      ...prev,
      isDraft: !isCustomsSubmission,
    }))
  }, [])

  const updateTransitEditorData = useCallback((partial: Partial<DeclarationEditorData>) => {
    setDeclarationEditorData((prev) => ({
      ...prev,
      ...partial,
    }))
  }, [])

  const setServerValidationResultForScope = useCallback((scope: NctsScope, requirements: ServerValidationResult | null) => {
    setServerValidationResult((prev) => ({
      ...prev,
      [scope]: requirements,
    }))
  }, [])

  const contextValue: DeclarationEditorContextValue = useMemo(() => ({
    declarationData: {
      transitOperation,
      consignment,
    },
    editorData: declarationEditorData,
    isLoading: !transitOperation && isFetching && isFetchingConsignment,
    updateTransitEditorData,
    serverValidationResult: serverValidationResult ?? {},
    setServerValidationResultForScope,
    setIsCustomsSubmission,
    validationContextRef,
  }), [
    transitOperation,
    consignment,
    declarationEditorData,
    isFetching,
    isFetchingConsignment,
    updateTransitEditorData,
    serverValidationResult,
    setServerValidationResultForScope,
    setIsCustomsSubmission,
    validationContextRef,
  ])

  if (is404(error)) {
    return <Navigate to="/not-found" replace state={{ from: location, id: transitId }} />
  }

  return (
    <DeclarationEditorContext.Provider value={contextValue}>
      {children}
    </DeclarationEditorContext.Provider>
  )
}

export function useDeclarationEditorContext() {
  const ctx = useContext(DeclarationEditorContext)
  if (!ctx) {
    throw new Error('useTransitEditorContext must be used inside <TransitEditorContextProvider>')
  }
  return ctx
}

export function useServerRequirementsContext(scope: NctsScope) {
  const ctx = useDeclarationEditorContext()
  const { serverValidationResult, setServerValidationResultForScope } = ctx

  const setServerValidationResult = useCallback(
    (validationResult: ServerValidationResult | null) => setServerValidationResultForScope(scope, validationResult),
    [scope, setServerValidationResultForScope],
  )

  return {
    serverValidationResult: serverValidationResult?.[scope] ?? {} as ServerValidationResult,
    setServerValidationResult,
  }
}
